import { productionMode } from '../core/environment.js';
import { lang } from '../lang.js';
import { patterns } from '../patterns/patterns.js';
import { beads } from '../beads.js';
import { settings } from '../settings.js';
import { menu } from '../menu.js';
import { palette } from '../palette.js';

// Fonts

const fontPath = productionMode ? 'https://beadographer.com/myfonts' : 'http://localhost:8080/fonts';

pdfMake.fonts = {
    FiraSansCondensed: {
        normal: `${fontPath}/FiraSansCondensed-Regular.ttf`,
        bold: `${fontPath}/FiraSansCondensed-Bold.ttf`
    }
};

export const exp = {};

exp.getDetails = () => {

    if (!settings.pattern) return [];

    let [myRows, myColumns] = menu.getDisplayRowsAndColumns();

    let result = [];

    if (!settings.pattern.noColumns) {
        result = [myRows.toString(), lang.get('rows'), 'x', myColumns.toString(), lang.get('columns') + '.\n'];
    }

    let measurements = exp.getMeasurements();

    return result.concat([
        beads.getBeadCount().toString(), lang.get('beads') + '.\n', lang.caps(lang.get('project dimensions')) + ':',
        measurements.cm.width.toFixed(2), 'x', measurements.cm.height.toFixed(2), lang.get('cm') + '\n',
        measurements.inch.width.toFixed(2), 'x', measurements.inch.height.toFixed(2), lang.get('inches') + '.\n'
    ]).join(' ');
};

exp.getMeasurements = () => {

    let [myRows, myColumns] = menu.getDisplayRowsAndColumns();

    let myLayout = patterns.getRowsAndColumns({
        pattern: settings.pattern,
        rows: myRows,
        columns: myColumns,
        size: myRows,
    });

    let myLength = (settings.beadDisplayHeight / 60) * settings.diameter;
    let myDimensions = settings.pattern.getDimensions(myLayout.rows, myLayout.columns, settings.diameter, myLength);

    return {
        cm: {
            width: myDimensions.width,
            height: myDimensions.height
        },
        inch: {
            width: myDimensions.width / 2.54,
            height: myDimensions.height / 2.54
        }
    };
};

exp.getBeadList = () => {

    const myTable = {
        style: 'tableExample',
        table: {
            headerRows: 1,
            body: [
                [
                    {text: lang.caps(lang.get('bead')), style: 'tableHeader'},
                    {text: lang.caps(lang.get('symbol')), style: 'tableHeader'},
                    {text: lang.caps(lang.get('count')), style: 'tableHeader'},
                    {text: lang.caps(lang.get('description')), style: 'tableHeader'}
                ],
            ]
        },
        layout: 'noBorders'
    };

    b3.store.state.swatches.forEach(swatch => {
        const myCount = swatch.getBeadCount();
        if (myCount) {
            myTable.table.body.push([
                {
                    image: swatch.rt.canvas.toDataURL(),
                    width: 20
                },
                swatch.letterCode,
                myCount.toString(),
                swatch.description
            ]);
        }
    });

    return [
        {text: lang.get('bead list pdf title', 'Bead List'), style: 'header'},
        myTable
    ];
};

exp.getTextContent = (content, style) => {
    // return content or null
    if (content) {
        return {
            text: content,
            style: style,
        };
    } else {
        return null;
    }
};

exp.createRT = () => {
    exp.rt = game.add.renderTexture(0, 0, 1040, 1400).setOrigin(0);
    exp.cursor = game.add.rectangle(0, 0, 28, 28, 0x000000, 1).setOrigin(0.5);
    exp.text = game.add.text(0, 0, '', {fontFamily: 'Verdana', fontSize: '24px', color: '#FFF'}).setOrigin(0.5);
};

exp.getFooter = ({currentPage, options}) => {
    return {
        margin: [10, -10],
        columns: [
            { text: currentPage.toString(), alignment: 'left', margin: 15, width: 50},
            { text: options.exportFooter, alignment: 'right', margin: 15}
        ]
    };
};

exp.styleSheet = {
    header: {
        fontSize: 25,
        margin: [0, 15, 0, 15],
        bold: true
    },
    headerBreak: {
        fontSize: 25,
        margin: [0, 15, 0, 15],
        bold: true
    },
    subheader: {
        fontSize: 15,
        bold: true,
        margin: [0, 5, 0, 15],
    },
    tableExample: {
        margin: [0, 5, 0, 15],
    },
    diagram: {
        fontSize: 7,
        margin: [0, 5, 0, 15],
    },
    tableHeader: {
        bold: true,
        fontSize: 13,
    },
    wordChart: {
        lineHeight: 1.3
    },
    paragraphText: {
        margin: [0, 5, 0, 15],
    },
    myBreak: {
        margin: [0, -15, 0, -15],
    },
    logo: {
        alignment: 'right'
    }
};

exp.getTitle = () => ({
    text: settings.title,
    style: 'header'
});

exp.getCredits = () => [
    {
        text: 'Credits',
        style: 'header'
    },
    {
        text: settings.credits,
        style: 'paragraphText'
    }
];

exp.getInstructions = () => [
    {
        text: 'Beading Instructions',
        style: 'header'
    },
    {
        text: settings.instructions,
        style: 'paragraphText'
    }
];

const shortcodes = [
    {
        key: 'title',
        replaceWith: () => exp.getTitle()
    },
    {
        key: 'description',
        replaceWith: () => settings.description
    },
    {
        key: 'details',
        replaceWith: () => exp.getDetails()
    },
    {
        key: 'credits',
        replaceWith: () => exp.getCredits()
    },
    {
        key: 'instructions',
        replaceWith: () => exp.getInstructions()
    },
    {
        key: 'bead list',
        replaceWith: ({options}) => exp.getBeadList({options})
    },
    {
        key: 'image',
        replaceWith: () => exp.getProjectImage()
    },
    {
        key: 'diagram',
        replaceWith: ({options}) => exp.getDiagramsOnly({options})
    },
    {
        key: 'word chart',
        replaceWith: ({options}) => exp.getWordChartsOnly({options})
    },
    {
        key: 'page break',
        replaceWith: () => ({text: ' ', style: 'myBreak'})
    },
];

const getShortCode = ({str, options}) => {
    const shortcode = shortcodes.find(obj => obj.key === str.toLowerCase());

    if (!shortcode) {
        const myImage = options.userImages.find(element => element.name === str);

        if (!myImage) {
            return {
                text: `Shortcode not recognized: [${str}]`,
                style: 'paragraphText'
            };
        }

        return {
            image: myImage.data,
            fit: [500, 500],
            style: 'paragraphText'
        }
    }

    return shortcode.replaceWith({options});
};

const combinify = arr => {
    const result = [];

    let chunk = [];

    arr.forEach(element => {
        let needsCombining = false;
        if (typeof element === 'string') {
            needsCombining = true;
        } else if (element.bold && !element.style) {
            needsCombining = true;
        }

        if (needsCombining) {
            chunk.push(element);
        } else {
            if (chunk.length) {
                result.push({
                    text: [...chunk],
                    style: 'paragraphText'
                });
                chunk = [];
            }
            result.push(element);
        }
    });

    if (chunk.length) {
        result.push({
            text: [...chunk],
            style: 'paragraphText'
        });
    }

    return result;
};

// convert 'hello [friend] world' => ['hello ', 'friend', ' world']
const parseStr = str => str.split('[').map(x => x.split(']')).flat();

exp.parseEditor = ({options}) => {
    const result = [];

    const elementsArr = document.querySelector('[trix-id="2"]')
        .editor.getDocument()
        .blockList.objects;

    elementsArr.forEach(element => {

        const isHeading = element.attributes?.[0] === 'heading1';

        const myContents = [];

        element.text.pieceList.objects.forEach(obj => {

            const list = parseStr(obj.string);

            const renderStr = str => {
                const trimmed = str.trim();
                if (!trimmed) return;

                if (isHeading) return {
                    text: str,
                    style: 'header'
                }
                if (obj.attributes.values.bold) return {
                    text: str,
                    bold: true
                }
                return str;
            }

            list.forEach((str, i) => {
                const thingToAdd = (i % 2 === 1) ?
                    getShortCode({str, options}):
                    renderStr(str);

                if (thingToAdd) myContents.push(thingToAdd);
            });
        });

        const _myContents = combinify(myContents);

        result.push(_myContents);
    });

    return result;
};


exp.pdf = ({options}) => {

    palette.assignSymbols({preserve: true}); // make sure no swatches are blank

    const defaultStyle = { font: 'FiraSansCondensed' };
    const styles = exp.styleSheet;
    const content = exp.parseEditor({options});
    const pageSize = options.exportPageSize === 'A4' ? 'A4' : 'LETTER';
    const footer = currentPage => exp.getFooter({currentPage, options});
    const header = options.headerImage ?
        currentPage => currentPage === 1 ?
            ({ image: options.headerImage,  width: 150, style: 'logo' }):
            null:
        null;
    const pageBreakBefore = (currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) => {
        if (currentNode.style === 'myBreak') return true;
        if (currentNode.style === 'headerBreak') return true;
        if (currentNode.style === 'header' && followingNodesOnPage.length === 0) return true;
        if (currentNode.style === 'subheader' && followingNodesOnPage.length === 0) return true;
        return false;
    };

    const config = {
        defaultStyle, styles, content, pageSize, header, footer, pageBreakBefore
    };

    let myPDF;

    try {
        myPDF = pdfMake.createPdf(config);
    } catch (error) {
        console.error(error);
    }

    return myPDF;
};
